// @architect-approved: INFRASTRUCTURE - App root does not follow component identity pattern
/**
 * App.tsx - RUNTIME DATABASE-DRIVEN ROUTING
 * 
 * ROUTE FORMAT: /component/{component_external_id}
 * Example: /component/2A-07615-07-31-4-TAB
 * 
 * ARCHITECTURE: Netflix/Citrix Streaming Model
 * - Database IS the router (Component_Registry.Register_Ready=1)
 * - Runtime queries database on-demand
 * - Components load from warehouse: RegisteredComponents/{cypher}.tsx
 * - NO static route generation, NO routes.json
 * 
 * CYPHER STRUCTURE (component_external_id):
 * - Component: 2A-{ID}-{Dept}-{DeptGroup}-{Hierarchy}-{Type}
 * - Example: 2A-07615-07-31-4-TAB
 * 
 * DISPLAY:
 * - Routes are cyphers (machine-readable, agent-proof)
 * - UI shows Friendly_Name from Component_Registry metadata
 * - Breadcrumbs built from database hierarchical relationships
 * 
 * AGENT-PROOF DESIGN:
 * - No routes.json → nothing for agents to "fix"
 * - Single route pattern: /component/:cypherID
 * - Database modifications blocked by pre-commit hooks
 * - See: docs/agent-contracts/AGENT_ROUTING_CONTRACT.md
 * 
 * Created: 2026-03-13 (Phase 4: Runtime Routing - Agent Bottleneck Solved)
 */

import { Box, CircularProgress } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { Suspense, useEffect, useRef, memo } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// MainStreet - Critical Entry Points (eager loaded, never change)
import { Login } from './MainStreet/Authentication'
import MobileAppDeveloperPortal from './TGC/RegisteredTabs/scr_tgc_11928_mobile_app_developer_portal'
import { ExecutiveQRLanding, ExecutiveQuickMenu } from './MainStreet/ExecutiveAccess'
import DefaultLandingLoader from './MainStreet/Loaders/DefaultLandingLoader'
import { SkybridgeTram } from './MainStreet/Skybridge'
import { TGWelcomeCenter } from './MainStreet/WelcomeCenters'

// Branding system (now provides dynamic theme via BrandingProvider)
import { BrandingProvider } from './StylesLibrary/hooks/useBrandingContext'

// Global authentication guard (requires login for all routes except public)
import { AuthGuard } from './utils/AuthGuard'

// Route-tree error boundary (catches render/chunk-load failures that would
// otherwise unmount the whole app into a black screen; shows a fallback UI
// in place - Try Again / Go Home are manual, user-initiated only, no
// automatic reload or redirect anywhere in the platform).
import ErrorBoundary from './TGC/RegisteredTabs/scr_tgc_10756_error_boundary'

// Sequence route loader for /component/:seq routing
import { SequenceRouteLoader } from './utils/SequenceRouteLoader'

// API service utilities (sequence-based resolution)
import { loadRouterManifest } from './utils/apiService'

// GWALL Universal Renderer (Glass Wall Architecture - Proof of Concept)
import UniversalRenderer from './components/UniversalRenderer'

// Encoded route loader
import { generateEncodedRoutes } from './RouteLoaderEncoded'

// Marketing site (techgacity.com root route) — resolved via the same
// manifest-driven sequence resolution every other route uses.
import { resolveComponentBySeq } from './utils/componentResolver'
const TechgacityMarketingSite = resolveComponentBySeq(10022)

// Platform Header - Single Source of Truth for Org Context
import PlatformHeader from './components/PlatformHeader'

// Bug Reporter FAB - Quick issue reporting for SuperAdmin (bottom-left red button)
import BugReporterFAB from './components/BugReporterFAB'

/**
 * isMarketingDomain — true only when the browser's hostname is the public
 * marketing domain (techgacity.com / www.techgacity.com). Every other
 * hostname (mytechgacity.com portal domain, *.azurestaticapps.net,
 * localhost dev) falls through to the normal authenticated portal.
 */
const isMarketingDomain = (): boolean => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'techgacity.com' || host === 'www.techgacity.com';
};

/**
 * AppContent - Inner component with routing logic
 * Separated to access useLocation hook (must be inside BrowserRouter)
 *
 * 🔒 LOCKED (2026-09-30): Wrapped in React.memo. Takes zero props, so this
 * makes AppContent immune to re-renders caused purely by its ANCESTORS
 * re-rendering (App → BrandingProvider → BrowserRouter → AppContent).
 * Without this, every BrandingProvider render (its fetchCollection()
 * setLoading/setCollection churn - see useBrandingContext.tsx) forced the
 * ENTIRE route tree - AuthGuard, SequenceRouteLoader, and whatever it mounts -
 * to re-render too, dozens to hundreds of times in a row. This starved out
 * Suspense/lazy children from ever getting a chance to commit before the next
 * re-render invalidated the in-progress mount, producing stale/never-updating
 * content on some navigations (a render-storm race, not a routing bug).
 * AppContent now only re-renders due to ITS OWN hooks (useLocation) or
 * context/state it actually consumes. Do not remove this memo() wrapper.
 */
const AppContent = memo(function AppContent() {
  const location = useLocation();

  // PUBLIC ROUTES: No platform header, allow scrolling (marketing sites, login, welcome)
  const publicRoutes = [
    '/welcome', 
    '/login', 
    '/skybridge', 
    '/qr', 
    '/component/10021', 
    '/component/10022',
    '/mkt_cab_10021_caboose_website',
    '/mkt_tgc_10022_techgacity_website',
    '/developer-portal'
  ];
  // On techgacity.com, '/' itself IS the marketing site — treat it as public too.
  const isPublicRoute =
    publicRoutes.some(route => location.pathname.startsWith(route)) ||
    (isMarketingDomain() && location.pathname === '/');
  
  return (
    <AuthGuard>
      {/* FLEX-FIRST LEGO BLOCK: App-level layout */}
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        
        {/* Platform Header - ALWAYS RENDERED (app-level authentication) */}
        <PlatformHeader />
        
        {/* Router Outlet — single flex-fill layer (flattened 2026-09-19).
            `flex: 1` + `minHeight: 0` is the robust nested-flexbox fill
            pattern: it gives every routed module ONE unambiguous height
            boundary to inherit from, instead of relaying through a second
            `height: '100%'` Box (which needs THIS box's height to already
            be resolved first — a redundant indirection that was the root
            cause of a fixmod_ grid silently collapsing to height:0 on this
            route while rendering fine inside ComponentViewer's Dialog).
            See /memories/repo/fixmod-grid-height-collapse-live-route-bug-2026-09-19.md */}
        <Box sx={{ 
          flex: 1, 
          minHeight: 0,
          overflow: 'auto',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}>
            <ErrorBoundary>
            <Suspense fallback={
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            }>
            <Routes>
              {/* MainStreet - Critical Entry Points (Manually Maintained) */}
              {/* techgacity.com root ('/') shows the marketing site; every other
                  hostname (mytechgacity.com, *.azurestaticapps.net, localhost)
                  falls through to the normal database-driven portal landing page. */}
              <Route path="/" element={isMarketingDomain() ? <TechgacityMarketingSite /> : <DefaultLandingLoader />} />
              <Route path="/welcome" element={<TGWelcomeCenter />} />
              <Route path="/login" element={<Login />} />
              <Route path="/developer-portal" element={<MobileAppDeveloperPortal />} />
              <Route path="/skybridge" element={<SkybridgeTram />} />
              <Route path="/executive-access" element={<ExecutiveQuickMenu />} />
              <Route path="/qr/:qrCode" element={<ExecutiveQRLanding />} />
              
              {/* SEQUENCE-BASED COMPONENT ROUTING (dev/utility routes only)
                  Pattern: /component/:seq
                  
                  Manifest-driven routing (prebuild):
                  - generate-component-seq-map.js creates sequence → filename mapping
                  - SequenceRouteLoader reads manifest, uses createComponentBySeq() for dynamic import
                  - Example: /component/10814 → mod_tgc_10814_organization_registry.tsx
                  
                  Used by internal dev-tool components (dependency tree viewer, nav
                  quick-menus, favorites list) for ad-hoc sequence-based deep links.
                  NOT used for directory-manifest button navigation - directory/module
                  buttons always navigate to a component's own directRoute below
                  (see docs/agent-contracts/BLUEPRINT_NAVIGATION_ARCHITECTURE.md and
                  src/utils/directoryNavigation.ts).
              */}
              <Route path="/component/:seq" element={<SequenceRouteLoader />} />
              
              {/* GWALL UNIVERSAL RENDERER (PROOF OF CONCEPT)
                  
                  SQL-driven component rendering - Glass Wall Architecture:
                  - SQL generates complete component definition (fields + buttons + data)
                  - Frontend just renders what SQL sends (zero hardcoded business logic)
                  - Device-agnostic: Same SQL serves web, mobile, watch, TV
                  - Test URL: /universal/10886 (White-Label Branding Profile)
                  
                  Purpose: Prove Netflix + Citrix baby pattern works
                  Future: Replace 1100 TSX files with SQL-driven components
              */}
              <Route path="/universal/:seq" element={
                <UniversalRenderer seq={parseInt(window.location.pathname.split('/')[2])} />
              } />
              
              {/* FILENAME-BASED ROUTES (LEGACY)
                  
                  Database queries at runtime (no static generation):
                  - Component_Registry.Register_Ready = 1 → component is routable
                  - Component loads from: RegisteredComponents/{cypherID}.tsx
                  - Metadata (Friendly_Name, etc.) injected at render time
                  
                  Agent-proof architecture:
                  - No routes.json to "fix"
                  - No prebuild generation
                  - Database IS the routing manifest
                  
                  See: docs/agent-contracts/AGENT_ROUTING_CONTRACT.md
              */}
              {generateEncodedRoutes()}
              
              {/* 404 Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
        </Box>
        
        <BugReporterFAB />
        <ToastContainer position="top-right" autoClose={3000} />
      </Box>
    </AuthGuard>
  );
});

function App() {
  // Get current org UUID from localStorage (set during login org selection)
  // Falls back to default UUID if not logged in
  const currentOrgUuid = localStorage.getItem('active_organization_uuid') || '00000000-0000-0000-0000-000000000000';

  // Load router manifest at app startup (sequence-based API resolution)
  useEffect(() => {
    loadRouterManifest();
  }, []);

  // Suppress browser extension errors (Copilot, React DevTools, etc.)
  // These are harmless but create console noise
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || '';
      if (message.includes('message channel closed') || 
          message.includes('Extension context invalidated')) {
        event.preventDefault(); // Suppress extension-related errors
      }
    };
    
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, []);

  return (
    <BrandingProvider orgId={currentOrgUuid}>
      <BrowserRouter>
        <CssBaseline />
        <AppContent />
      </BrowserRouter>
    </BrandingProvider>
  )
}

export default App
